import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Code, TrendingUp, Coffee, Zap, Globe, Heart, ArrowUpRight } from 'react-feather';
import Footer from '../../components/Footer';

export default function Careers() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Zap size={24} />,
      title: "Speed Matters",
      desc: "Nos movemos rápido. Lanzamos, medimos y mejoramos en días, no meses."
    },
    {
      icon: <Globe size={24} />,
      title: "Remote First Culture",
      desc: "Trabaja desde Lima, Cusco o tu casa. Nos importan los resultados, no la silla."
    },
    {
      icon: <Heart size={24} />,
      title: "Obsesión por el Usuario",
      desc: "No construimos lo que queremos, construimos lo que la comunidad necesita."
    }
  ];

  const positions = [
    {
      role: "Full Stack Developer",
      type: "Full-time",
      loc: "Remoto / Lima",
      dept: "Engineering",
      icon: <Code size={20} className="text-purple-400" />
    },
    {
      role: "Growth Marketing Manager",
      type: "Full-time",
      loc: "Lima (Híbrido)",
      dept: "Marketing",
      icon: <TrendingUp size={20} className="text-pink-400" />
    },
    {
      role: "Sales Representative (Venues)",
      type: "Commission Based",
      loc: "Lima",
      dept: "Sales",
      icon: <Coffee size={20} className="text-yellow-400" />
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-purple-500 selection:text-white overflow-x-hidden">
      
      {/* 1. TEXTURA DE RUIDO */}
      <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.04] mix-blend-overlay" 
           style={{ backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")' }}>
      </div>

      {/* 2. BACKGROUND ORBS (Violeta/Azul para Tech) */}
      <div className="fixed top-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[100px] pointer-events-none" />

      {/* HEADER NAV */}
      <div className="fixed top-0 left-0 w-full z-40 px-6 py-6 flex justify-between items-center pointer-events-none">
        <button 
            onClick={() => navigate(-1)} 
            className="pointer-events-auto flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest text-white/50 hover:text-white transition-colors group uppercase bg-black/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/5"
        >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Volver
        </button>
      </div>

      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* --- HERO SECTION --- */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-purple-400 font-mono text-sm tracking-widest uppercase mb-4 block">
                Join the Team
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tighter text-white leading-[0.95]">
              CONSTRUYE EL <br/> FUTURO DE LA <span className="text-white/20">NOCHE.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light leading-relaxed mb-10">
              No somos una ticketera más. Somos una empresa de tecnología que está reinventando cómo las personas conectan en la vida real. Buscamos builders, no empleados.
            </p>
          </motion.div>

          {/* --- CULTURE GRID --- */}
          <div className="grid md:grid-cols-3 gap-8 mb-32">
            {values.map((item, i) => (
                <motion.div 
                    key={i}
                    className="p-8 border-l border-white/10 hover:border-purple-500/50 transition-colors duration-300 bg-gradient-to-r from-white/[0.02] to-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                    <div className="mb-6 text-white/80">{item.icon}</div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
            ))}
          </div>

          {/* --- OPEN POSITIONS --- */}
          <motion.div 
            className="mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-end justify-between mb-12 border-b border-white/10 pb-4">
                <h2 className="text-3xl font-bold">Posiciones Abiertas</h2>
                <span className="text-white/30 text-sm font-mono hidden md:block">3 ROLES DISPONIBLES</span>
            </div>

            <div className="space-y-4">
                {positions.map((job, i) => (
                    <motion.a 
                        href={`mailto:talent@goclub.social?subject=Aplicación para ${job.role}`}
                        key={i}
                        className="group flex flex-col md:flex-row md:items-center justify-between p-6 bg-[#0A0A0A] border border-white/5 rounded-xl hover:border-white/20 hover:bg-white/5 transition-all cursor-pointer"
                        whileHover={{ x: 5 }}
                    >
                        <div className="flex items-start gap-4 mb-4 md:mb-0">
                            <div className="p-3 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
                                {job.icon}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors">{job.role}</h3>
                                <div className="flex items-center gap-3 mt-1 text-sm text-white/40 font-mono">
                                    <span>{job.dept}</span>
                                    <span>•</span>
                                    <span>{job.loc}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-between w-full md:w-auto md:gap-8">
                            <span className="text-xs font-bold px-3 py-1 rounded-full border border-white/10 text-white/60 bg-black">
                                {job.type}
                            </span>
                            <ArrowUpRight className="text-white/30 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </div>
                    </motion.a>
                ))}
            </div>
          </motion.div>

          {/* --- SPONTANEOUS APPLICATION CTA --- */}
          <motion.div 
            className="relative overflow-hidden rounded-3xl bg-purple-900/10 border border-purple-500/20 p-8 md:p-16 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
             <div className="relative z-10 max-w-xl mx-auto">
                <h2 className="text-3xl font-bold mb-4">¿No ves tu rol ideal?</h2>
                <p className="text-white/60 mb-8 font-light">
                    Siempre estamos buscando talento excepcional. Si eres un crack en lo que haces y crees que puedes aportar a AFTR, queremos conocerte.
                </p>
                <a 
                    href="mailto:talent@goclub.social?subject=Candidatura Espontánea"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold rounded-full hover:bg-purple-50 transition-colors"
                >
                    MÁNDANOS TU PORTFOLIO
                </a>
             </div>
             
             {/* Background Glow inside card */}
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-purple-600/20 blur-[80px] pointer-events-none" />
          </motion.div>

        </div>
      </div>
      <Footer />
    </div>
  );
}